import { lazy, Suspense, type ReactNode } from "react";
import { useT } from "../i18n/catalogue.js";

import { EmptyState, LoadingState } from "../states/index.js";
import { isEmptyBurnDown, type BurnDownStep } from "./burn-down.js";

/**
 * What is still to go out over a period — a STEP line, not a smooth one (feature 024, research R9).
 *
 * Scheduled debits are discrete: the remaining amount holds between due dates and drops on them. A smooth
 * line would say money drains away continuously, which is the one thing a schedule is not; a bar would say
 * the days between have no value, when they hold the same remaining amount as the day before. Before
 * `today` the line is muted ("gone out"), from `today` it is solid, and a payment due today is on the solid
 * side (clarification Q3).
 *
 * A DESIGN-SYSTEM COMMITMENT (`dashboard.md` Non-Goals): it is here because no existing mark draws a step —
 * every area and line in this package is `monotone` — and bending `StackedAreaChart` into one would change a
 * component two features were verified against, to draw a mark neither uses.
 *
 * It holds no strings of its own; every label arrives as a prop, in the reader's language.
 */
const Impl = lazy(() =>
  import("./BurnDownChartImpl.js").then((module) => ({ default: module.BurnDownChartImpl })),
);

export interface BurnDownChartProps {
  steps: readonly BurnDownStep[];
  window: { from: string; to: string };
  today: string;
  /** Names the muted half ("gone out") and the solid half ("still to go out"), and labels the marker. */
  pastLabel: string;
  remainingLabel: string;
  todayLabel: string;
  ariaLabel: string;
  formatValue: (cents: number) => string;
  formatTick: (day: string) => string;
  /** The days to label on the axis — every day for a week, a few for a month, each month's first for a year. */
  ticks: readonly string[];
  /** What leaves on a day, for the tooltip (FR-016). Null when nothing does. */
  renderTooltip?: (day: string) => ReactNode;
  emptyMessage: ReactNode;
  /**
   * The Dashboard's size: shorter, no value axis and no legend. The card states the figures in words directly
   * above it, so the axis would repeat them in a smaller font, and the legend's two names fit no card width.
   */
  compact?: boolean;
}

export function BurnDownChart(props: BurnDownChartProps): ReactNode {
  const t = useT();
  // Checked BEFORE the boundary, so a period with nothing scheduled never downloads the library.
  if (isEmptyBurnDown(props.steps)) return <EmptyState>{props.emptyMessage}</EmptyState>;

  return (
    <Suspense fallback={<LoadingState>{t("ui.chart.loading")}</LoadingState>}>
      <Impl {...props} />
    </Suspense>
  );
}

export { isEmptyBurnDown };
